import {KeyValueCache} from '@multipass-system';
import {Logger} from 'esp-js';

const _log = Logger.create('LocalStorageCache');

export class LocalStorageCache implements KeyValueCache {
    readonly #keyPrefix: string;
    readonly #storage: Storage;

    constructor(keyPrefix: string, storage: Storage = window.localStorage) {
        this.#keyPrefix = keyPrefix;
        this.#storage = storage;
    }

    getValue<TValue>(key: string): TValue | undefined {
        return this.#run(`read key '${key}'`, () => {
            const stored = this.#storage.getItem(this.#storageKey(key));
            return stored === null ? undefined : JSON.parse(stored) as TValue;
        });
    }

    setValue<TValue>(key: string, value: TValue): void {
        this.#run(`write key '${key}'`, () => this.#storage.setItem(this.#storageKey(key), JSON.stringify(value)));
    }

    hasValue(key: string): boolean {
        return this.#run(`read key '${key}'`, () => this.#storage.getItem(this.#storageKey(key)) !== null) ?? false;
    }

    clear(): void {
        const prefix = this.#storageKey('');
        this.#run('clear cache', () => {
            const owned = Object.keys(this.#storage).filter(key => key.startsWith(prefix));
            owned.forEach(key => this.#storage.removeItem(key));
        });
    }

    #storageKey(key: string): string {
        return `${this.#keyPrefix}-${key}`;
    }

    // localStorage can throw (quota, sandboxed/private-mode origins) and stored JSON can be corrupt; a
    // cache must degrade rather than crash its callers, so every access funnels through here.
    #run<TResult>(description: string, action: () => TResult): TResult | undefined {
        try {
            return action();
        } catch (error) {
            _log.error(`Failed to ${description}`, error);
            return undefined;
        }
    }
}
